import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useForm } from "react-hook-form";
import th from "dayjs/locale/th";
import dayjs from "dayjs";

import { boxStyle } from "../../constants";
import { OverwriteAdapterDayjs } from "../../utils/common.util";
import { useAppDispatch, useAppSelector } from "../../state/hook";
import { getReportMemberActivityHistoryService } from "../../services/report.service";
import { StyledTableCell, StyledTableRow } from "../../shared/style";
import { setApp } from "../../state/libs/appSlice";
import {
  getActivityTypesService,
  getProvinceService,
} from "../../services/masterData.service";
import { getTask, setTask } from "../../state/libs/taskSlice";

const contentToolbar = {
  toolbar: {
    title: "ส่งผลออกกำลังกาย",
    subtitles: ["ส่งผลรายครั้ง"],
  },
};

const MemberActivityHistory = () => {
  const dispatch = useAppDispatch();
  const taskState = useAppSelector(getTask);

  const [histories, setHistories] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);
  const [provincesFittered, setProvincesFittered] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [flag, setFlag] = useState<boolean>(false);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fromDate, setFromDate] = useState<string | null>(null);
  const [toDate, setToDate] = useState<string | null>(null);

  const { register, getValues, setValue } = useForm({
    defaultValues: {
      activity_type_id: "",
      member_id: "",
      from_date: "",
      to_date: "",
      province_id: "",
      zone: "",
    },
  });

  const handleOnSearch = () => {
    getReportMemberActivitySummaries();
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleExportReport = () => {
    dispatch(
      setTask({
        id: "memberActivityHistoryReport",
        reportName: "memberActivityHistoryReport",
        process: true,
        processing: false,
        filters: getValues(),
      })
    );
  };

  const handleFromDateChange = (value: any) => {
    const date = dayjs(value).format("YYYY-MM-DD");
    setFromDate(date);
    setValue("from_date", date);
  };

  const handleToDateChange = (value: any) => {
    const date = dayjs(value).format("YYYY-MM-DD");
    setToDate(date);
    setValue("to_date", date);
  };

  const handleZoneChange = (event: SelectChangeEvent) => {
    setProvincesFittered(
      !event.target.value
        ? provinces
        : provinces.filter((t: any) => t.zone === parseInt(event.target.value))
    );
    setValue("province_id", "");
  };

  const getReportMemberActivitySummaries = () => {
    if (!flag) {
      setFlag(true);
      return;
    }
    setLoading(true);
    getReportMemberActivityHistoryService({
      size: rowsPerPage,
      page: page + 1,
      ...getValues(),
    }).then((resp) => {
      setLoading(false);
      setHistories(resp.data.items);
      setCount(resp.data.total_row);
    });
  };

  const getActivityTypes = () => {
    getActivityTypesService().then((resp) => {
      setActivities(resp.data);
    });
  };

  const getProvinces = () => {
    getProvinceService().then((resp) => setProvinces(resp.data));
  };

  const setContentToolbar = () => {
    dispatch(setApp(contentToolbar));
  };

  useEffect(setContentToolbar, [dispatch]);

  useEffect(getReportMemberActivitySummaries, [getValues, page, rowsPerPage]);

  useEffect(getActivityTypes, []);

  useEffect(getProvinces, []);

  return (
    <Box display="flex" flexDirection="column" gap="16px">
      <Box sx={boxStyle}>
        <Box
          display="flex"
          justifyContent="space-between"
          gap="16px"
          flexDirection={{ xs: "column", md: "row" }}
        >
          <Box display="flex" flexDirection="column" gap={4} width={1}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="zone-label">เขตสุขภาพ</InputLabel>
                  <Select
                    labelId="zone-label"
                    id="province"
                    label="เขตสุขภาพ"
                    {...register("zone")}
                    onChange={handleZoneChange}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {Array.from(Array(13).keys()).map((index: any) => (
                      <MenuItem key={index} value={(index + 1).toString()}>
                        {"เขตสุขภาพที่ " + (index + 1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="province-label">จังหวัด</InputLabel>
                  <Select
                    labelId="province-label"
                    id="province"
                    label="จังหวัด"
                    {...register("province_id")}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {provincesFittered.map(
                      (p: { id: number; name: string }) => (
                        <MenuItem key={p.id} value={p.id.toString()}>
                          {p.name}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="activity-type">ประเภทกีฬา</InputLabel>
                  <Select
                    labelId="activity-type"
                    id="activity_type"
                    label="ประเภทกีฬา"
                    {...register("activity_type_id")}
                  >
                    <MenuItem key={0} value="">
                      ทั้งหมด
                    </MenuItem>
                    {activities.map((row: any, index: any) => (
                      <MenuItem key={index} value={row.id}>
                        {row.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <TextField
                    fullWidth
                    type="text"
                    label="EBIB CODE"
                    placeholder="ค้นหา EBIB CODE"
                    {...register("member_id")}
                  />
                </FormControl>
              </Grid>
            </Grid>

            <Grid container spacing={4}>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={OverwriteAdapterDayjs}
                    adapterLocale={th}
                  >
                    <DatePicker
                      label="วันเริ่ม"
                      views={["year", "month", "day"]}
                      inputFormat="DD/MMM/BBBB"
                      value={fromDate}
                      onChange={handleFromDateChange}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "วว/ดด/ปปปป",
                            readOnly: true,
                          }}
                          {...register("from_date", { required: true })}
                        />
                      )}
                    ></DatePicker>
                  </LocalizationProvider>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <LocalizationProvider
                    dateAdapter={OverwriteAdapterDayjs}
                    adapterLocale={th}
                  >
                    <DatePicker
                      label="วันสิ้นสุด"
                      views={["year", "month", "day"]}
                      inputFormat="DD/MMM/BBBB"
                      value={toDate}
                      onChange={handleToDateChange}
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          inputProps={{
                            ...params.inputProps,
                            placeholder: "วว/ดด/ปปปป",
                            readOnly: true,
                          }}
                          {...register("to_date", { required: true })}
                        />
                      )}
                    ></DatePicker>
                  </LocalizationProvider>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Box display="flex" gap="8px" height="fit-content">
            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon sx={{ color: "white" }} />}
                color="green"
                disabled={loading}
                onClick={handleOnSearch}
              >
                <Typography color="white">ค้นหา</Typography>
              </Button>
              {loading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>

            <Box sx={{ position: "relative" }}>
              <Button
                variant="contained"
                color="blue"
                disabled={taskState.tasks.find(
                  (task: any) => task.id === "memberActivityHistoryReport"
                )}
                onClick={handleExportReport}
              >
                <Typography color="white">EXPORT</Typography>
              </Button>
              {taskState.tasks.find(
                (task: any) => task.id === "memberActivityHistoryReport"
              ) && (
                <CircularProgress
                  color="blue"
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={boxStyle} width={1}>
        <TableContainer
          component={Paper}
          sx={{
            boxShadow: "none",
            borderRadius: 0,
            overflow: "auto",
          }}
        >
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <StyledTableCell>ลำดับ</StyledTableCell>
                <StyledTableCell>EBIB CODE</StyledTableCell>
                <StyledTableCell>ชื่อ</StyledTableCell>
                <StyledTableCell>นามสกุล</StyledTableCell>
                <StyledTableCell>เขตสุขภาพ</StyledTableCell>
                <StyledTableCell>จังหวัด</StyledTableCell>
                <StyledTableCell>อำเภอ</StyledTableCell>
                <StyledTableCell>ตำบล</StyledTableCell>
                <StyledTableCell>หมู่ที</StyledTableCell>
                <StyledTableCell>บ้านเลขที่/หมู่บ้าน/ซอย/ถนน</StyledTableCell>
                <StyledTableCell>วันที่ส่งผล</StyledTableCell>
                <StyledTableCell>ประเภทกีฬา</StyledTableCell>
                <StyledTableCell>เวลา</StyledTableCell>
                <StyledTableCell>ระยะทาง</StyledTableCell>
                <StyledTableCell>ระดับความเหนื่อย</StyledTableCell>
                <StyledTableCell>จำนวนแคลอรี่</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {histories.map((row, index) => (
                <StyledTableRow key={row.name}>
                  <StyledTableCell component="th" scope="row">
                    {(page - 1) * rowsPerPage + rowsPerPage + (index + 1)}
                  </StyledTableCell>
                  <StyledTableCell>{row.member_id}</StyledTableCell>
                  <StyledTableCell>{row.first_name}</StyledTableCell>
                  <StyledTableCell>{row.last_name}</StyledTableCell>
                  <StyledTableCell>{row.zone}</StyledTableCell>
                  <StyledTableCell>{row.province}</StyledTableCell>
                  <StyledTableCell>{row.district}</StyledTableCell>
                  <StyledTableCell>{row.subdistrict}</StyledTableCell>
                  <StyledTableCell>{row.moo}</StyledTableCell>
                  <StyledTableCell>{row.address}</StyledTableCell>
                  <StyledTableCell>
                    {dayjs(row.activity_date).format("DD MMM BBBB")}
                  </StyledTableCell>
                  <StyledTableCell>{row.activity_type}</StyledTableCell>
                  <StyledTableCell align="right">
                    {row.minutes?.toLocaleString() || 0}
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    {row.distances?.toLocaleString() || 0}
                  </StyledTableCell>
                  <StyledTableCell>{row.intense_level}</StyledTableCell>
                  <StyledTableCell align="right">
                    {row.calories?.toLocaleString() || 0}
                  </StyledTableCell>
                </StyledTableRow>
              ))}
              {!histories.length && (
                <StyledTableRow>
                  <StyledTableCell
                    component="th"
                    scope="row"
                    colSpan={16}
                    align="center"
                  >
                    ไม่พบข้อมูล
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={count}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Box>
    </Box>
  );
};

export default MemberActivityHistory;
